import * as React from "react";
import { useEnvironmentService } from "../../../services/registry/EnvironmentService";
import type { IEnvironment } from "../../../types/EnvironmentTypes";
import {
  FeatureValue,
  FeatureItem,
  determineFeatureType,
  AP_HIDDEN_FEATURES,
  AP_DEFAULT_TICKED_CHECKBOXES,
} from "../../../utils/Features";

let featuresCache: FeatureItem[] | null = null;
let promise: Promise<FeatureItem[]> | null = null;

export interface FeaturesLoaderProps {
  environment: IEnvironment;
  onFeaturesLoaded: (features: FeatureItem[]) => void;
  onError: (error: string) => void;
}

// Component that throws a promise while loading features
export const FeaturesLoader: React.FC<FeaturesLoaderProps> = ({ environment, onFeaturesLoaded, onError }) => {
  const environmentService = useEnvironmentService();

  const loadFeatures = async (): Promise<FeatureItem[]> => {
    const response = await environmentService.getAllFeatures(environment.name);
    const featuresData = response.features || {};

    const hiddenFeatures = new Set([
      ...((featuresData[AP_HIDDEN_FEATURES] || []) as string[]),
      AP_HIDDEN_FEATURES,
      AP_DEFAULT_TICKED_CHECKBOXES,
    ]);

    const featureItems: FeatureItem[] = Object.entries(featuresData).map(([key, value]) => {
      const type = determineFeatureType(value as FeatureValue);
      return {
        key,
        originalValue: value as FeatureValue,
        currentValue: value as FeatureValue,
        type,
        hasChanged: false,
        isHidden: hiddenFeatures.has(key),
      };
    });

    // Sort features alphabetically by key
    featureItems.sort((a, b) => a.key.localeCompare(b.key));
    return featureItems;
  };

  if (featuresCache !== null) {
    onFeaturesLoaded(featuresCache);
    return null;
  }

  if (promise) {
    throw promise;
  }
  promise = loadFeatures();
  promise
    .then((featureItems) => {
      featuresCache = featureItems;
      onFeaturesLoaded(featureItems);
    })
    .catch(() => {
      featuresCache = [];
      onError("Failed to load features.");
    });
  throw promise;
};

export const clearFeaturesCache = () => {
  featuresCache = null;
  promise = null;
};
